import React, { useCallback, useEffect, useState } from 'react';
import { useDashboardLocale } from '../shared/dashboardLocale';
import { u } from '../shared/hardcodedKoEn';
import { ThreeLineStack } from '../shared/ThreeLineStack';
import { kiwoomH3, kiwoomMuted } from './kiwoomTheme';
import KiwoomEquityCurveChart from './KiwoomEquityCurveChart';
import {
  KIWOOM_DT_CONFIG,
  KIWOOM_DT_START_EQUITY,
  fetchDayTradingPaper,
  hmOf,
  resolveSegments,
  todayKSTDate,
  type KiwoomDayTradingSnapshot,
  type KiwoomDtDay,
  type KiwoomDtFill,
  type KiwoomDtSegment,
} from './kiwoomDayTradingPaper';

const th: React.CSSProperties = {
  textAlign: 'left',
  color: '#a371f7',
  fontSize: '0.7rem',
  padding: '6px 8px',
  borderBottom: '1px solid #7c3aed44',
  whiteSpace: 'nowrap',
};
const td: React.CSSProperties = {
  color: '#f3e8ff',
  fontSize: '0.75rem',
  padding: '6px 8px',
  borderBottom: '1px solid #7c3aed22',
  verticalAlign: 'top',
};

const POS = '#3fb950';
const NEG = '#f85149';
const ACCENT = '#f0b90b';
const POLL_MS = 60_000;

function Stat({
  label, value, color, title, sub,
}: {
  label: string;
  value: React.ReactNode;
  color?: string;
  title?: string;
  sub?: React.ReactNode;
}) {
  return (
    <div className="kiwoom-pairs-stat-item" title={title}>
      <div className="kiwoom-pairs-stat-label">{label}</div>
      <div className="kiwoom-pairs-stat-value" style={{ color: color ?? '#f3e8ff' }}>{value}</div>
      {sub != null && (
        <div style={{ marginTop: 2, fontSize: '0.66rem', color: kiwoomMuted, lineHeight: 1.35 }}>{sub}</div>
      )}
    </div>
  );
}

function krw(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '–';
  return Math.round(n).toLocaleString('ko-KR');
}

function pct(n: number | null | undefined, digits = 2): string {
  if (n == null || Number.isNaN(n)) return '–';
  return `${n >= 0 ? '+' : ''}${n.toFixed(digits)}%`;
}

function signedKrw(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '–';
  const s = krw(Math.abs(n));
  return n >= 0 ? `+${s}` : `-${s}`;
}

type SegMode = 'live' | 'backtest';

const KiwoomDayTradingPaperPanel: React.FC = () => {
  const { locale, t } = useDashboardLocale();
  const [snap, setSnap] = useState<KiwoomDayTradingSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [segMode, setSegMode] = useState<SegMode>('live');

  const load = useCallback(async () => {
    try {
      const s = await fetchDayTradingPaper();
      setSnap(s);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSnap(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = window.setInterval(() => { load(); }, POLL_MS);
    return () => window.clearInterval(id);
  }, [load]);

  const { backtest, live, updatedAt } = resolveSegments(snap);
  const hasLive = live.days.length > 0;
  // live 아직 없으면 자동으로 백테스트 원장 보여 주되, 탭은 live 대기 표시
  const activeMode: SegMode = segMode === 'live' && !hasLive ? 'backtest' : segMode;
  const shown: KiwoomDtSegment = activeMode === 'live' ? live : backtest;
  const rows: KiwoomDtDay[] = [...shown.days].reverse();
  const fillSrc = shown.fills?.length ? shown.fills : (shown.fills_today ?? []);
  const fills: KiwoomDtFill[] = [...fillSrc].reverse();
  const cfg = snap?.config ?? KIWOOM_DT_CONFIG;
  const startEq = snap?.start_equity_krw ?? KIWOOM_DT_START_EQUITY;

  // 실전 계좌 숫자는 항상 live 기준(Perpetual Live equity 칸처럼)
  const liveEquity = live.equity_krw ?? live.summary.final_equity_krw ?? startEq;
  const liveTotal = (liveEquity / startEq - 1) * 100;
  const liveSum = live.summary;
  const shownSum = shown.summary;
  const dd = liveSum.drawdown_pct ?? 0;
  const todayPnl = hasLive ? (liveSum.today_pnl_krw ?? 0) : null;
  const asOf = hasLive
    ? (live.days[live.days.length - 1]?.date ?? todayKSTDate())
    : (live.start_date ?? todayKSTDate());

  return (
    <div
      className="kiwoom-daytrading-panel"
      style={{
        padding: '14px 16px',
        borderRadius: 8,
        border: '1px solid #7c3aed66',
        background: 'linear-gradient(135deg, rgba(124,58,237,0.12) 0%, rgba(0,0,0,0.35) 100%)',
      }}
    >
      <div className="binance-alert-header">
        <div className="binance-alert-header-copy">
          <h3 style={{ ...kiwoomH3, marginTop: 0, marginBottom: 4 }}>Day trading Paper</h3>
          <div style={{ color: kiwoomMuted, fontSize: '0.72rem', lineHeight: 1.45 }}>
            {t('kiwoom.daytrading.subtitle.line1')}
            {' · '}
            {`${cfg.rule} · N=${cfg.n} · dip −${cfg.dip_pct}% · crt≤${cfg.crt_max} · fee ${cfg.fee_pct}%`}
            {' · '}
            {t('kiwoom.daytrading.subtitle.line3')}
            {updatedAt ? (
              <span style={{ marginLeft: 8 }}>
                · updated {updatedAt.slice(11, 16)} UTC
              </span>
            ) : null}
          </div>
        </div>
        <button
          type="button"
          className="kiwoom-pairs-refresh"
          onClick={() => { setLoading(true); load(); }}
          title={t('common.refresh')}
        >
          {t('common.refresh')}
        </button>
      </div>

      {loading && !snap && (
        <div style={{ color: kiwoomMuted, fontSize: '0.78rem' }}>{t('common.loading')}</div>
      )}
      {error && (
        <div style={{ color: NEG, fontSize: '0.78rem', marginBottom: 8 }}>{error}</div>
      )}

      {/* —— Equity hero (Perpetual Live 스타일) —— */}
      <div className="kiwoom-pairs-stats" style={{ marginTop: 8 }}>
        <Stat
          label="equity"
          value={krw(liveEquity)}
          color={liveEquity >= startEq ? POS : NEG}
          title={t('kiwoom.daytrading.stat.equity.title')}
          sub={(
            <>
              {t('kiwoom.daytrading.stat.start')} {krw(startEq)}
              {' · '}
              <span style={{ color: liveTotal >= 0 ? POS : NEG }}>{pct(liveTotal)}</span>
            </>
          )}
        />
        <Stat
          label={t('kiwoom.daytrading.stat.today')}
          value={todayPnl == null ? '–' : signedKrw(todayPnl)}
          color={todayPnl == null ? kiwoomMuted : todayPnl >= 0 ? POS : NEG}
          sub={hasLive ? liveSum.today_date ?? asOf : t('kiwoom.daytrading.awaiting')}
        />
        <Stat
          label="realized"
          value={signedKrw(live.realized_pnl_krw ?? liveEquity - startEq)}
          color={(live.realized_pnl_krw ?? 0) >= 0 ? POS : NEG}
        />
        <Stat
          label={u("잔고밀림")}
          value={hasLive ? pct(dd, 1) : '–'}
          color={!hasLive ? kiwoomMuted : dd <= -5 ? NEG : dd < 0 ? ACCENT : POS}
          title={t('kiwoom.daytrading.stat.dd.title')}
          sub={hasLive ? `MDD ${liveSum.mdd_pct.toFixed(1)}%` : undefined}
        />
        <Stat
          label="win"
          value={hasLive && (liveSum.taken ?? 0) > 0
            ? `${(liveSum.win_rate * 100).toFixed(0)}%`
            : '–'}
          color={ACCENT}
          sub={hasLive
            ? `${liveSum.wins ?? 0}/${liveSum.taken ?? 0} · skip ${liveSum.skipped ?? 0}`
            : undefined}
        />
        <Stat
          label="days"
          value={hasLive ? `${liveSum.pos_days}/${liveSum.n_days}` : '0'}
          sub={`as of ${asOf}`}
        />
        <Stat label="slots" value={`N=${cfg.n}`} sub={`${cfg.rule} · paper`} />
        <Stat label="mode" value={snap?.mode ?? 'paper'} sub={snap?.strategy ?? 'theme-dip-limit'} />
      </div>

      {/* —— Equity curve —— */}
      <div style={{ marginTop: 10, display: 'flex', gap: 12, alignItems: 'center', fontSize: '0.68rem', color: kiwoomMuted }}>
        <span><span style={{ color: '#c4a0ff', fontWeight: 700 }}>—</span> {t('kiwoom.daytrading.legend.live')}</span>
        <span><span style={{ color: '#7c3aed', fontWeight: 700 }}>┄</span> {t('kiwoom.daytrading.legend.backtest')}</span>
        {!hasLive && (
          <span style={{ color: ACCENT }}>· {t('kiwoom.daytrading.awaiting')}</span>
        )}
      </div>
      <div style={{ marginTop: 4, borderRadius: 6, border: '1px solid #7c3aed33', padding: '6px 4px', background: 'rgba(0,0,0,0.25)' }}>
        <KiwoomEquityCurveChart
          startEquity={startEq}
          backtest={backtest.days.map((d) => d.equity_krw)}
          live={live.days.map((d) => d.equity_krw)}
          backtestCurve={backtest.equity_curve}
          liveCurve={live.equity_curve}
        />
      </div>

      {/* —— Live / Backtest 탭 —— */}
      <div style={{ display: 'flex', gap: 8, marginTop: 14, alignItems: 'center' }}>
        <button
          type="button"
          className="kiwoom-pairs-refresh"
          style={{
            opacity: segMode === 'live' ? 1 : 0.55,
            borderColor: segMode === 'live' ? '#c4a0ff' : undefined,
          }}
          onClick={() => setSegMode('live')}
        >
          {t('kiwoom.daytrading.tab.live')}{hasLive ? ` (${live.days.length})` : ''}
        </button>
        <button
          type="button"
          className="kiwoom-pairs-refresh"
          style={{
            opacity: segMode === 'backtest' ? 1 : 0.55,
            borderColor: segMode === 'backtest' ? '#7c3aed' : undefined,
          }}
          onClick={() => setSegMode('backtest')}
        >
          {t('kiwoom.daytrading.tab.backtest')} ({backtest.days.length})
        </button>
        <span style={{ color: kiwoomMuted, fontSize: '0.68rem', marginLeft: 4 }}>
          {activeMode === 'backtest' && segMode === 'live'
            ? t('kiwoom.daytrading.awaiting')
            : `${shownSum.taken ?? 0} trades · ${pct(shownSum.total_ret_pct)} · win ${((shownSum.win_rate ?? 0) * 100).toFixed(0)}%`}
        </span>
      </div>

      {/* —— Trades (Perpetual Live 이벤트 피드와 같은 자리) —— */}
      <div className="kiwoom-pairs-section-title" style={{ marginTop: 12 }}>
        {t('kiwoom.daytrading.section.trades')}
        <span style={{ color: kiwoomMuted, fontWeight: 400, marginLeft: 8 }}>
          {fills.length}{t('kiwoom.daytrading.section.trades.count')}
        </span>
      </div>
      <div className="binance-alert-table-wrap" style={{ maxHeight: 360, overflow: 'auto' }}>
        <table key={`dt-fills-${locale}-${activeMode}`} className="binance-alert-table" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th style={{ ...th, width: '28%' }}>{t('kiwoom.daytrading.table.symbol')}</th>
              <th style={{ ...th, width: '18%' }}>{t('kiwoom.daytrading.table.side')}</th>
              <th style={{ ...th, width: '26%' }}>{t('kiwoom.daytrading.table.qty')}</th>
              <th style={{ ...th, width: '28%' }}>{t('common.table.pnl')}</th>
            </tr>
          </thead>
          <tbody>
            {loading && fills.length === 0 && (
              <tr><td colSpan={4} style={{ ...td, color: kiwoomMuted, textAlign: 'center' }}>{t('common.loading')}</td></tr>
            )}
            {!loading && fills.length === 0 && (
              <tr><td colSpan={4} style={{ ...td, color: kiwoomMuted, textAlign: 'center' }}>{t('kiwoom.daytrading.table.trades.empty')}</td></tr>
            )}
            {fills.map((f, i) => {
              const pnl = f.pnl_krw;
              const net = f.net_pct;
              const win = (pnl ?? net ?? 0) > 0;
              const loss = (pnl ?? net ?? 0) < 0;
              return (
                <tr key={`${f.symbol}-${f.time}-${i}`} className={f.status === 'open' ? 'broker-row-open' : undefined}>
                  <td style={{ ...td, wordBreak: 'break-word' }}>
                    <ThreeLineStack
                      mutedColor={kiwoomMuted}
                      lines={[
                        f.symbol,
                        f.day ? f.day.slice(5) : null,
                        f.time ? hmOf(f.time) : null,
                      ]}
                    />
                  </td>
                  <td style={td}>
                    <ThreeLineStack
                      mutedColor={kiwoomMuted}
                      lines={[
                        <span style={{ color: POS, fontWeight: 600 }}>{f.side ?? 'BUY'}</span>,
                        f.rule ?? cfg.rule,
                        f.status === 'open' ? 'OPEN' : (f.exit_time ? `exit ${hmOf(f.exit_time)}` : null),
                      ]}
                    />
                  </td>
                  <td style={td}>
                    <ThreeLineStack
                      mutedColor={kiwoomMuted}
                      lines={[
                        f.quantity != null ? `${f.quantity}` : '–',
                        f.price != null ? krw(f.price) : null,
                        f.hold_min != null ? `${f.hold_min.toFixed(0)}m` : null,
                      ]}
                    />
                  </td>
                  <td style={td}>
                    <ThreeLineStack
                      mutedColor={kiwoomMuted}
                      lines={[
                        pnl != null ? (
                          <span style={{ color: win ? POS : loss ? NEG : '#f3e8ff', fontWeight: 600 }}>
                            {signedKrw(pnl)}
                          </span>
                        ) : '–',
                        net != null ? (
                          <span style={{ color: win ? POS : loss ? NEG : kiwoomMuted }}>
                            {pct(net)}
                          </span>
                        ) : null,
                        f.alloc_krw != null ? `alloc ${krw(f.alloc_krw)}` : null,
                      ]}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* —— Daily ledger —— */}
      <div className="kiwoom-pairs-section-title" style={{ marginTop: 14 }}>
        {activeMode === 'live' ? t('kiwoom.daytrading.section.live') : t('kiwoom.daytrading.section.backtest')}
      </div>
      <div className="binance-alert-table-wrap">
        <table key={`dt-days-${locale}-${activeMode}`} className="binance-alert-table" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th style={{ ...th, width: '18%' }}>{t('kiwoom.daytrading.table.date')}</th>
              <th style={{ ...th, width: '14%' }}>{t('kiwoom.daytrading.table.signals')}</th>
              <th style={{ ...th, width: '14%' }}>{t('kiwoom.daytrading.table.taken')}</th>
              <th style={{ ...th, width: '12%' }}>{t('kiwoom.daytrading.table.win')}</th>
              <th style={{ ...th, width: '20%' }}>{t('common.table.pnl')}</th>
              <th style={{ ...th, width: '22%' }}>{t('kiwoom.daytrading.table.equity')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={6} style={{ ...td, color: kiwoomMuted, textAlign: 'center' }}>{t('kiwoom.daytrading.awaiting')}</td></tr>
            )}
            {rows.map((d) => {
              const skip = d.skipped ?? Math.max(0, d.signals - d.taken);
              return (
                <tr key={d.date}>
                  <td style={td}>
                    <ThreeLineStack mutedColor={kiwoomMuted} lines={[d.date.slice(5), null, null]} />
                  </td>
                  <td style={td}>
                    <ThreeLineStack
                      mutedColor={kiwoomMuted}
                      lines={[d.signals, skip > 0 ? `skip ${skip}` : null, null]}
                    />
                  </td>
                  <td style={td}>
                    <ThreeLineStack mutedColor={kiwoomMuted} lines={[d.taken, `${d.wins}W`, null]} />
                  </td>
                  <td style={td}>
                    <ThreeLineStack
                      mutedColor={kiwoomMuted}
                      lines={[d.taken > 0 ? `${Math.round((d.wins / d.taken) * 100)}%` : '–', null, null]}
                    />
                  </td>
                  <td style={td}>
                    <ThreeLineStack
                      mutedColor={kiwoomMuted}
                      lines={[
                        <span style={{ color: d.pnl_krw >= 0 ? POS : NEG, fontWeight: 600 }}>
                          {signedKrw(d.pnl_krw)}
                        </span>,
                        pct(d.ret_pct),
                        null,
                      ]}
                    />
                  </td>
                  <td style={td}>
                    <ThreeLineStack mutedColor={kiwoomMuted} lines={[krw(d.equity_krw), null, null]} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ color: kiwoomMuted, fontSize: '0.68rem', marginTop: 10, lineHeight: 1.5 }}>
        {t('kiwoom.daytrading.disclaimer')}
      </div>
    </div>
  );
};

export default KiwoomDayTradingPaperPanel;
